import axios from "axios";
import {
  CompanyBalanceSheet,
  CompanyCashFlow,
  CompanyCompData,
  CompanyIncomeStatement,
  CompanyKeyMetrics,
  CompanyProfile,
  CompanySearch,
  CompanyTenK,
} from "./company";

interface SearchResponse {
  data: CompanySearch[];
}

const api = process.env.REACT_APP_API_URL || "http://localhost:8080/api/";

const getToken = () => {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
};

export const searchCompanies = async (query: string) => {
  try {
    const data = await axios.get<SearchResponse>(`${api}stock/search/${query}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.log("error message: ", error.message);
      return error.message;
    } else {
      console.log("unexpected error: ", error);
      return "An unexpected error has occurred";
    }
  }
};

export const getCompanyProfile = async (query: string) => {
  try {
    const data = await axios.get<CompanyProfile[]>(
      `https://financialmodelingprep.com/stable/profile?symbol=${query}&exchange=NASDAQ&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
  }
};

export const getKeyMetrics = async (query: string) => {
  try {
    const data = await axios.get<CompanyKeyMetrics[]>(
      `https://financialmodelingprep.com/stable/key-metrics-ttm?symbol=${query}&exchange=NASDAQ&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
    throw new Error(error.response?.data?.message || error.message || "Unknown API error");
  }
};

export const getIncomeStatement = async (query: string) => {
  try {
    const data = await axios.get<CompanyIncomeStatement[]>(
      `https://financialmodelingprep.com/stable/income-statement?symbol=${query}&exchange=NASDAQ&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
    throw new Error(error.response?.data?.message || error.message || "Unknown API error");
  }
};

export const getBalanceSheet = async (query: string) => {
  try {
    const data = await axios.get<CompanyBalanceSheet[]>(
      `https://financialmodelingprep.com/stable/balance-sheet-statement?symbol=${query}&exchange=NASDAQ&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
    throw new Error(error.response?.data?.message || error.message || "Unknown API error");
  }
};

export const getCashflowStatement = async (query: string) => {
  try {
    const data = await axios.get<CompanyCashFlow>(
      `https://financialmodelingprep.com/stable/cash-flow-statement?symbol=${query}&exchange=NASDAQ&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
    throw new Error(error.response?.data?.message || error.message || "Unknown API error");
  }
};

export const getCompDate = async (query: string) => {
  try {
    const data = await axios.get<CompanyCompData[]>(
      `https://financialmodelingprep.com/stable/stock-peers?symbol=${query}&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
    throw new Error(error.response?.data?.message || error.message || "Unknown API error");
  }
};

const formatDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
    2,
    "0"
  )}`;

export const today = formatDate(new Date()); // "YYYY-MM-DD"

export const tenMonthsAgo = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() - 10);
  return formatDate(d);
})();

export const getTenK = async (query: string) => {
  try {
    const data = await axios.get<CompanyTenK[]>(
      `https://financialmodelingprep.com/stable/sec-filings-search/symbol?symbol=${query}&from=${tenMonthsAgo}&to=${today}&page=0&limit=1&apikey=${process.env.FMP_KEY}`
    );
    return data;
  } catch (error: any) {
    console.log("error message from api: ", error.message);
    throw new Error(error.response?.data?.message || error.message || "Unknown API error");
  }
};
